import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Box, Typography, ToggleButton, ToggleButtonGroup, Chip } from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import * as API from '../../api';

const generateTextTemplate = (extras) => {
    if (!extras || extras.length === 0) return '';
    const supplierName = extras[0].supplier_name || 'NCC';
    let text = `[Fola Travel] Đặt dịch vụ - ${supplierName} - ${extras.length} booking\nFola Travel xin gửi yêu cầu đặt ${extras.length} dịch vụ như sau:\n==================================\n`;
    extras.forEach((e, i) => {
        text += `${i + 1}. Khách: ${e.customer_detail?.name || 'Khách lẻ'} - SĐT: ${e.customer_detail?.phone || ''}\n   Mã BK: ${e.bk_code}\n   Dịch vụ: ${e.title} (SL: ${e.quantity})\n   Ngày SD: ${e.service_date ? e.service_date.split('-').reverse().join('/') : '---'}\n   Ghi chú: ${e.note || ''}\n----------------------------------\n`;
    });
    text += `\nĐại lý: Fola Travel - 0363463666 hoặc 0986332366 (Mr. Hùng).\nQuý công ty vui lòng xác nhận đặt chỗ tại đây.\nTrân trọng,`;
    return text;
};

const generateHtmlTemplate = (extras) => {
    if (!extras || extras.length === 0) return '';
    const thStyle = `border: 1px solid #333; padding: 5px; background-color: #f2f2f2;`;
    const tdStyle = `border: 1px solid #333; padding: 5px; vertical-align: top;`;
    
    const rows = extras.map(e => `
            <tr>
                <td style="${tdStyle}; text-align: center;">${e.bk_code}</td>
                <td style="${tdStyle}">${e.customer_detail?.name || 'Khách lẻ'} <br/> ${e.customer_detail?.phone || ''}</td>
                <td style="${tdStyle}">${e.title} <b>(SL: ${e.quantity})</b></td>
                <td style="${tdStyle}; text-align: center; font-weight: bold; color: #ed6c02;">${e.service_date ? e.service_date.split('-').reverse().join('/') : '---'}</td>
                <td style="${tdStyle}">${e.note || ''}</td>
            </tr>`).join('');

    return `<html>
<body style="font-family: Arial; line-height: 1.6;">
    <div>Fola Travel xin gửi yêu cầu đặt ${extras.length} dịch vụ:</div>
    
    <table style="width: 100%; border-collapse: collapse; font-size: 13px; margin-top: 10px;">
        <thead>
            <tr>
                <th style="${thStyle}">Mã BK</th>
                <th style="${thStyle}">Khách hàng</th>
                <th style="${thStyle}">Dịch vụ (SL)</th>
                <th style="${thStyle}">Ngày SD</th>
                <th style="${thStyle}">Ghi chú</th>
            </tr>
        </thead>
        <tbody>${rows}
        </tbody>
    </table>

    <div style="margin-top: 15px;"><strong>Đại lý: Fola Travel</strong></div>
    <div>--------------------------------------------------</div>
    <div style="margin-top: 10px;">Mọi thông tin chi tiết vui lòng liên hệ số hotline Fola Travel tại 0363463666 hoặc 0986332366 (Mr. Hùng).</div>
    <div>Quý công ty vui lòng xác nhận đặt chỗ qua email này.</div>
    <div style="margin-top: 10px;">Trân trọng,</div>
</body>
</html>`;
};

const ExtraOrderDialog = ({ open, onClose, selectedExtras, showToast, onSuccess }) => {
    const [textContent, setTextContent] = useState('');
    const [htmlContent, setHtmlContent] = useState('');
    const [recipientEmail, setRecipientEmail] = useState('');
    const [ccEmail, setCcEmail] = useState('');
    const [emailSubject, setEmailSubject] = useState('');
    const [viewMode, setViewMode] = useState('EMAIL');
    const [sending, setSending] = useState(false);

    useEffect(() => {
        if (open && selectedExtras.length > 0) {
            setTextContent(generateTextTemplate(selectedExtras));
            setHtmlContent(generateHtmlTemplate(selectedExtras));
            const firstE = selectedExtras[0];
            setRecipientEmail(firstE.supplier_email || '');
            setCcEmail(firstE.supplier_cc_emails || '');
            setEmailSubject(`[Fola Travel] - Đặt dịch vụ - Ngày ${firstE.service_date ? firstE.service_date.split('-').reverse().join('/') : ''} - ${selectedExtras.length} BK`);
            setViewMode('EMAIL');
        }
    }, [open, selectedExtras]);

    const handleSendEmail = async () => {
        setSending(true);
        try {
            await API.client.post('/booking-extras/send_extra_order_email/', { email: recipientEmail, cc: ccEmail, subject: emailSubject, content: htmlContent });
            await Promise.all(selectedExtras.map(e => API.client.patch(`/booking-extras/${e.id}/update_status/`, { operation_status: 'CONTACTED' })));
            if (showToast) showToast('Đã gửi mail đặt dịch vụ thành công!', 'success');
            if (onSuccess) onSuccess();
            onClose();
        } catch (error) { if (showToast) showToast('Lỗi gửi mail.', 'error'); } finally { setSending(false); }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle sx={{ bgcolor: '#1976d2', color: 'white' }}><Typography variant="h6">LỆNH ĐẶT DỊCH VỤ VÀ PHÁT SINH ({selectedExtras.length} dịch vụ)</Typography></DialogTitle>
            <DialogContent dividers sx={{ display: 'flex', flexDirection: 'row', gap: 3, height: '520px' }}>
                
                {/* DANH SÁCH DỊCH VỤ ĐỒNG BỘ BÊN TRÁI */}
                <Box sx={{ width: '340px', minWidth: '340px', borderRight: '1px solid #e0e0e0', pr: 2, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#1976d2', textTransform: 'uppercase', pb: 0.5, borderBottom: '2px solid #1976d2' }}>
                        Danh sách dịch vụ chọn
                    </Typography>
                    {selectedExtras.map((e, idx) => (
                        <Box key={e.id || idx} sx={{ p: 1.5, border: '1px solid #e0e0e0', borderRadius: 1, bgcolor: '#f9f9f9', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                                <Chip label={e.bk_code || 'N/A'} size="small" color="secondary" variant="outlined" sx={{ fontWeight: 'bold', height: '20px' }} />
                                <Typography variant="caption" color="textSecondary">Mục {idx + 1}</Typography>
                            </Box>
                            <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#2c3e50' }}>
                                Khách: {e.customer_detail?.name || 'Khách lẻ'} {e.customer_detail?.phone ? `(${e.customer_detail.phone})` : ''}
                            </Typography>
                            <Typography variant="caption" display="block" sx={{ mt: 0.5, color: '#555' }}>
                                <b>Dịch vụ yêu cầu:</b> {e.title} <span style={{ color: '#9c27b0', fontWeight: 'bold' }}>(SL: {e.quantity})</span>
                            </Typography>
                            <Typography variant="caption" display="block" color="textSecondary">
                                <b>Ngày sử dụng:</b> {e.service_date ? e.service_date.split('-').reverse().join('/') : '---'}
                            </Typography>
                            {e.note && (
                                <Typography variant="caption" display="block" sx={{ color: '#ed6c02', fontStyle: 'italic', mt: 0.5, bgcolor: '#fff3e0', p: 0.5, borderRadius: '4px' }}>
                                    <b>Ghi chú điều hành:</b> {e.note}
                                </Typography>
                            )}
                        </Box>
                    ))}
                </Box>

                {/* KHU VỰC SOẠN THẢO NỘI DUNG BÊN PHẢI */}
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box>
                        <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#555' }}>
                            Tiêu đề:
                        </Typography>
                        <TextField 
                            fullWidth size="small" 
                            value={emailSubject} 
                            onChange={(e) => setEmailSubject(e.target.value)}
                            placeholder="Tiêu đề..."
                        />
                    </Box>
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#555' }}>
                                {viewMode === 'EMAIL' ? 'Nội dung Email:' : 'Nội dung Text:'}
                            </Typography>
                            <ToggleButtonGroup value={viewMode} exclusive onChange={(e, v) => v && setViewMode(v)} size="small" sx={{ bgcolor: '#f5f5f5', height: '32px' }}>
                                <ToggleButton value="EMAIL" sx={{ px: 2, fontSize: '0.75rem', fontWeight: 'bold', textTransform: 'none' }}><EmailIcon sx={{ mr: 1, fontSize: '1rem' }} /> Email (Bảng)</ToggleButton>
                                <ToggleButton value="TEXT" sx={{ px: 2, fontSize: '0.75rem', fontWeight: 'bold', textTransform: 'none' }}><TextSnippetIcon sx={{ mr: 1, fontSize: '1rem' }} /> Text (Copy)</ToggleButton>
                            </ToggleButtonGroup>
                        </Box>
                        {viewMode === 'EMAIL' ? (
                            <TextField multiline fullWidth value={htmlContent} onChange={(e) => setHtmlContent(e.target.value)} sx={{ flex: 1, bgcolor: '#fff3e0', '& .MuiInputBase-root': { height: '100%', alignItems: 'flex-start', fontFamily: 'monospace', fontSize: '0.85rem' } }} />
                        ) : (
                            <TextField multiline fullWidth value={textContent} onChange={(e) => setTextContent(e.target.value)} sx={{ flex: 1, bgcolor: '#f9fbe7', '& .MuiInputBase-root': { height: '100%', alignItems: 'flex-start', fontFamily: 'monospace', fontSize: '0.9rem' } }} />
                        )}
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions sx={{ p: 2, bgcolor: '#f9f9f9' }}>
                <Box sx={{ flex: 1, display: 'flex', gap: 1 }}>{viewMode === 'EMAIL' && <><TextField fullWidth size="small" label="Gửi tới (Email Nhà cung cấp)" value={recipientEmail} onChange={(e) => setRecipientEmail(e.target.value)} /><TextField fullWidth size="small" label="CC Email" value={ccEmail} onChange={(e) => setCcEmail(e.target.value)} /></>}</Box>
                <Button onClick={onClose} color="inherit">Đóng</Button>
                {viewMode === 'TEXT' && <Button variant="contained" color="success" onClick={() => { navigator.clipboard.writeText(textContent); if (showToast) showToast('Đã copy nội dung bản text thành công!', 'success'); }}>Copy Bản Text</Button>}
                {viewMode === 'EMAIL' && <Button variant="contained" onClick={handleSendEmail} disabled={sending || !recipientEmail}>Gửi lệnh dịch vụ</Button>}
            </DialogActions>
        </Dialog>
    );
};
export default ExtraOrderDialog;